import 'whatwg-fetch';
import * as PIXI from 'pixi.js';
import GameKitUI from './GameKitUI';
import Game, { ViewPortScaling } from './Game';
import Level from './Game/Level';
import LevelEditor from './GameKit/LevelEditor';
import gamekitStore from './GameKitUI/store';
import gameplayerStore from './GamePlayerUI/store';
import {
    updateGameSettings,
    updatePlayerSettings,
    createLevel,
    moveLevel,
    deleteLevel,
    updateLoadProgress,
    updateLevelThumbnail,
    setGameThumbnail,
    updateLevelDescription
} from './GameKitUI/actions/gameActions';
import { gameplayerUpdateLoadProgress } from './GamePlayerUI/actions/loadingActions';
import { playerClose, playerOpen, updateMMSessionData } from './GameKitUI/actions/gameKitActions';
import { setBottomBarText } from './GameKitUI/actions/bottomBarActions';
import { toggleGuide, forceSelectStep } from './GameKitUI/actions/gameGuideActions';
import { playerSettings } from './GameKit/shared';
import { Cursor } from './GameKit/Cursor';
import Loader from './Game/Base/Loader';
import { OpenPopup } from './GameKitUI/actions/popupActions';
import { PopupTypes, PopupStates } from './GameKitUI/components/Popup/Enums';
import AssetManager from './Game/Base/AssetManager';
import GameContext from './GameContext';
import GamePlayer from './GamePlayer';
import Vector from './Game/Base/Math/Vector';
import { setAllLevelSettings, setLevelSettings } from './GameKitUI/actions/levelSettingsActions';
import Global from './Game/Global';
import settings from './settings';
import { setUserLoggedIn } from './GameKitUI/actions/userActions';
import { GuideStepEnum } from './GameKitUI/shared';

class GameKit extends GameContext {
    constructor() {
        super();
        this.ui = new GameKitUI();
        this.game = new Game();

        this.gamePlayer = null;

        this.originalGameID = '';

        this.csrfToken = null;
        this.authHash = null;

        // FOR DEBUG PURPOSES ONLY
        window.gameKit = this;

        this.madeScreenshots = [];
        this.selectedScreenshot = 0;
        this.deletedLevels = [];

        this.gameID = '';
        this.mmSessionId = '';

        this.loginCallback = null;
        this.isCopy = true;

        this.lastSavedCopy = null;
    }

    init(settingsPath, gameID, isCopy, csrfToken, mmSessionId) {
        this.ui.render();

        this.mmSessionId = mmSessionId;
        this.loader = this.game.init(settingsPath);
        this.loader._onUpdate = (progress, text) => {
            const currentProgress = Math.round(progress * 100);
            gameplayerStore.dispatch(gameplayerUpdateLoadProgress(currentProgress, text));
            gamekitStore.dispatch(updateLoadProgress(currentProgress, text));
        }

        // this.mmSessionId = mmSessionId;
        this.gameID = gameID;
        this.csrfToken = csrfToken;

        const editorSprites = [
            { id: 'img.btn_copy', url: `${settings.assetsPath}img/btn_copy.png` },
            { id: 'img.btn_delete', url: `${settings.assetsPath}img/btn_delete.png` },
            { id: 'img.btn_rotate', url: `${settings.assetsPath}img/btn_rotate.png` },
            { id: 'img.btn_left', url: `${settings.assetsPath}img/btn_left.png` },
            { id: 'img.btn_right', url: `${settings.assetsPath}img/btn_right.png` },
            { id: 'img.btn_up', url: `${settings.assetsPath}img/btn_up.png` },
            { id: 'img.btn_down', url: `${settings.assetsPath}img/btn_down.png` },
            { id: 'img.move_to', url: `${settings.assetsPath}img/move_to.png` },
            { id: 'img.reaction_forward', url: `${settings.assetsPath}img/reaction_forward.png` },
            { id: 'img.invisible', url: `${settings.assetsPath}img/invisible.png` },
            { id: 'img.grid', url: `${settings.assetsPath}img/grid.png` },
            { id: 'img.btn_level_size_right', url: `${settings.assetsPath}img/pijl_level_grootte_r.png` },
            { id: 'img.btn_level_size_left', url: `${settings.assetsPath}img/pijl_level_grootte_l.png` },
            { id: 'img.btn_level_size_up', url: `${settings.assetsPath}img/pijl_level_grootte_u.png` },
            { id: 'img.btn_level_size_down', url: `${settings.assetsPath}img/pijl_level_grootte_d.png` }
        ];
        this.loader.addStage('Game Studio plaatjes ophalen', editorSprites.length, (finish, error, update) => {
            const assetLoaderInfo = AssetManager.loadDatabase(editorSprites, () => {
                finish();
            });
            assetLoaderInfo.onLoaded = () => {
                if (assetLoaderInfo.errors.length > 0) {
                    error(assetLoaderInfo.errors);
                } else update(assetLoaderInfo.loadCount);
            };
        });

        if (this.gameID && this.gameID !== '') {
            console.log('should load game');
            this.loader.addStage('Game laden', 10, (finish, error) => {
                console.log('loading game');
                this.loadGame(
                    this.gameID,
                    () => {
                        console.log('loaded game');
                        gamekitStore.dispatch(forceSelectStep(GuideStepEnum.LEVEL));
                        if (isCopy) {
                            this.originalGameID = gameID;
                            this.gameID = '';
                            this.game.gameID = '';
                        }
                        finish();
                    },
                    err => {
                        error(err);
                    },
                    !isCopy
                );
            });
        }

        this.loader.addStage('Laatste Checkjes', 1, (finish, error, update) => {
            window.LoginAPI.getHash(hash => {
                this.authHash = hash;
                if (hash) {
                    const username = window.sessionStorage.getItem('username');
                    if (username) this.setUser(username, true);
                    else this.setUser('Onbekend', true);
                }
                window.setTimeout(() => {
                    finish();
                }, 10);
            });
        });

        PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.LINEAR;

        this.initPixi('editor-canvas-wrapper', 'editor-canvas');

        this.gamePlayer = new GamePlayer(this.pixiApp, this.game, this.ui);

        this.loader.execute(
            () => {
                this.loaded();
            },
            error => {
                gamekitStore.dispatch(OpenPopup(PopupTypes.FAILEDLOAD));
                throw error;
            }
        );
        gamekitStore.dispatch(setBottomBarText(''));
    }

    getUserFromSession() {
        const username = window.sessionStorage.getItem('username');
        if (username !== null && username !== '') {
            this.setUser(username, true);
        } else this.setUser('Onbekend', true);
    }

	onLogin(event) {
		// onLogin is a message event. It handles messages form the LoginAPI 
		// Check if the message is the Login successful event
		if (event.data.event && (event.data.event == LoginAPI.events.LOGIN_SUCCESS)) {
	        window.LoginAPI.getHash(hash => {
	            this.authHash = hash;
	            if (hash) {
	            	this.getUserFromSession();
	                if (this.loginCallback) {
	                	this.loginCallback();
	                	this.loginCallback = null;
	                }
	            }
	        });
		}
    }

    loaded() {
        try {
            window.addEventListener("message", this.onLogin.bind(this), false);
        } catch {}

        gamekitStore.dispatch(updateMMSessionData(this.mmSessionId, this.game.settings.mmApiUrl));   

        this.updateGameSettings();
        this.levelEditor = new LevelEditor(this);

        this.game.createVisuals();
        this.updatePlayerSettings({});

        const that = this;
        window.addEventListener('beforeunload', function(e) {
            if (JSON.stringify(that.lastSavedCopy) !== JSON.stringify(that.game.serialize())) {
                const confirmationMessage = 'Weet je zeker dat je de GameKit wilt verlaten? Aanpassingen die niet zijn opgeslagen, gaan verloren.';
                (e || window.event).returnValue = confirmationMessage;
                return confirmationMessage;
            }
            return null;
        });

        window.requestAnimationFrame(this.render.bind(this));
    }

    render() {
        this.levelEditor.update(this.game.deltaTime);
        this.game.update();
        this.updateTimeout();
        this.gamePlayer.updateTimeout();
        window.requestAnimationFrame(this.render.bind(this));
    }

    moveRenderer() {
        document.getElementById('editor-canvas-wrapper').appendChild(this.pixiApp.view);
    }

    makeScreenshotsBase64() {
        console.log('convert screenshots');
        if (this.madeScreenshots) {
            for (let i = 0; i < this.madeScreenshots.length; i++) {
                this.imageToBase64(this.madeScreenshots[i].image, base64 => {
                    this.madeScreenshots[i].image = base64;
                });
            }
        }
    }

    imageToBase64(image, load) {
        const imageObj = new Image();
        imageObj.onload = () => {
            const copyCanvas = document.createElement('canvas');
            copyCanvas.width = imageObj.width;
            copyCanvas.height = imageObj.height;
            const copyCtx = copyCanvas.getContext('2d');
            copyCtx.drawImage(imageObj, 0, 0, copyCanvas.width, copyCanvas.height);
            load(copyCanvas.toDataURL());
        };
        imageObj.src = image;
    }

    levelHasScreenshot(uuid) {
        for (let i = 0; i < this.madeScreenshots.length; i++) {
            const item = this.madeScreenshots[i];
            if (item.uuid === uuid) {
                return true;
            }
        }
        return false;
    }

    makeScreenshot(manual = true) {
        const originalScaling = this.game.viewPortScaling;
        this.game.setViewPortScaling(ViewPortScaling.Contain);
        this.game.pixiApp.render();
        const copyCanvas = document.createElement('canvas');
        const snapshotScale = 4;
        copyCanvas.width = (Global.NATIVE_LEVEL_WIDTH * Global.NATIVE_BLOCK_SIZE) / snapshotScale;
        copyCanvas.height = (Global.NATIVE_LEVEL_HEIGHT * Global.NATIVE_BLOCK_SIZE) / snapshotScale;
        const copyCtx = copyCanvas.getContext('2d');

        copyCtx.drawImage(this.pixiApp.renderer.view, 0, 0, copyCanvas.width, copyCanvas.height);

        this.game.setViewPortScaling(originalScaling);

        if (manual) if (this.game.screenshotFlashSprite) this.game.screenshotFlashSprite.alpha = 1.5;

        const dat = {
            uuid: this.game.activeLevel.uuid,
            image: copyCanvas.toDataURL()
        };

        let found = false;
        for (let i = 0; i < this.madeScreenshots.length; i++) {
            const item = this.madeScreenshots[i];
            if (item.uuid === dat.uuid) {
                item.image = dat.image;
                found = true;
                break;
            }
        }
        if (!found) {
            this.madeScreenshots.push(dat);
        }
        gamekitStore.dispatch(updateLevelThumbnail(dat.uuid, dat.image));
        return dat.image;
    }

    dispatchScreenshots() {
        if (this.madeScreenshots) {
            for (let i = 0; i < this.madeScreenshots.length; i++) {
                gamekitStore.dispatch(updateLevelThumbnail(this.madeScreenshots[i].uuid, this.madeScreenshots[i].image));
            }
        }
        if (this.selectedScreenshot) {
            this.updateSelectedScreenshot(this.selectedScreenshot);
        }
    }

    // LevelUUID : String / null
    updateSelectedScreenshot(levelUUID, dispatch = true) {
        if(levelUUID === null) {
            gamekitStore.dispatch(setGameThumbnail(levelUUID));
            this.selectedScreenshot = 0;
            return;
        }
        
        for (let i = 0; i < this.madeScreenshots.length; i++) {
            const item = this.madeScreenshots[i];
            if (item.uuid === levelUUID) {
                this.selectedScreenshot = levelUUID;

                if (dispatch) {
                    gamekitStore.dispatch(setGameThumbnail(item.uuid));
                }
                return;
            }
        }
        throw new Error('Failed to update selected screenshot. LevelUUID does not match an existing level, or made screenshot.');
    }

    loginUser(onSuccess, onError) {
        window.LoginAPI.getHash(hash => {
            this.authHash = hash;
            if (hash) {
            	this.getUserFromSession();
                if (onSuccess) onSuccess();
            } else {
                this.loginCallback = onSuccess;
                window.LoginModal.show('login');
            }
        });
    }

    registerUser(callback) {
        window.LoginModal.show('register');
        callback();
    }

    setUser(username, loggedIn, dispatch = true) {
        if (dispatch) {
            gamekitStore.dispatch(setUserLoggedIn(username, loggedIn));
        }
    }

    setCursor(type) {
        switch (type) {
            case Cursor.def:
                this.canvas.style.cursor = 'default';
                break;
            case Cursor.grab:
                this.canvas.style.cursor = 'grab';
                break;
            case Cursor.grabbing:
                this.canvas.style.cursor = 'grabbing';
                break;
            case Cursor.pointer:
                this.canvas.style.cursor = 'pointer';
                break;
            default:
                this.canvas.style.cursor = 'default';
                break;
        }
    }

    // = // UI COMMUNICATION FUNCTIONS // = //

    // Get a level by UUID
    getLevel(levelUUID) {
        return this.game.levels.findByUUID(levelUUID);
    }

    // Open the level with the given UUID. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    openLevel(levelUUID, dispatch = true) {
        const level = this.game.levels.findByUUID(levelUUID);
        if (level == null) {
            console.warn(`Level with UUID "${levelUUID}"could not be found.`);
            return;
        }

        this.game.activeLevel = this.game.levels.setActive(this.game.levels.indexOf(level), this.game._hasVisuals, this.game._hasBody);
        this.levelEditor.detachFromLevel();
        this.levelEditor.attachToLevel(level);

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(setAllLevelSettings(this.levelEditor.getLevelSettings()));
            gamekitStore.dispatch(toggleGuide());
        }
    }

    // Close the currently opened level.
    closeLevel() {
        if (!this.game.activeLevel) return;
        this.game.activeLevel.disable();
    }

    // Create a level and return the new level. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    createNewLevel(dispatch = true) {
        const newLevel = new Level(this.game);
        newLevel.disable();

        const backgroundColors = [[0xff8800, 0xff0000], [0x0088ff, 0xffffff], [0xffff00, 0x6ac11f], [0x0989f2, 0x24dfe5], [0x222244, 0x6666ff]];
        const randomColor = backgroundColors[Math.floor(Math.random() * backgroundColors.length)];
        newLevel.backgroundColorA = randomColor[0];
        newLevel.backgroundColorB = randomColor[1];
        this.game.levels.push(newLevel);

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(createLevel(newLevel.uuid));
        }

        return newLevel;
    }

    dispatchLoadedLevels() {
        // Update de store
        for (let i = 0; i < this.game.levels.length; i++) {
            gamekitStore.dispatch(createLevel(this.game.levels[i].uuid));
        }
    }

    // Put a (levelUUID : string-uuid) level in a different spot in the level list (position : number). Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    moveLevel(levelUUID, position, dispatch = true) {
        const level = this.game.levels.findByUUID(levelUUID);
        const currentPosition = this.game.levels.indexOf(level);

        this.game.levels.splice(position, 0, this.game.levels.splice(currentPosition, 1)[0]);

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(moveLevel(currentPosition, position));
        }
    }

    // Copying a level will always dispatch an event. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    copyLevel(levelUUID, dispatch = true) {
        const newLevel = new Level(this.game);
        newLevel.disable();
        this.game.levels.push(newLevel);

        const { uuid } = newLevel;
        const deserialized = this.game.levels.findByUUID(levelUUID).serialize();
        newLevel.deserialize(deserialized);
        newLevel.uuid = uuid;

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(createLevel(newLevel.uuid));
        }

        return newLevel;
    }

    // Delete the level with the given UUID. Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch hould be false for calls from the store.
    deleteLevel(levelUUID, dispatch = true) {
        const level = this.game.levels.findByUUID(levelUUID);
        if (level == null) {
            console.warn(`Level with UUID "${levelUUID}"could not be found.`);
            return;
        }
        level.disable();

        const index = this.game.levels.indexOf(level);
        if (index < 0) {
            throw new Error('Level was not found in the level that the BlokManager is attached to.');
        }
        this.deletedLevels.push(level.uuid);
        for (let i = 0; i < this.madeScreenshots.length; i++) {
            const item = this.madeScreenshots[i];
            if (level.uuid === item.uuid) {
                this.madeScreenshots.splice(i, 1);
                i--;
            }
        }
        if (levelUUID === this.selectedScreenshot) {
            this.updateSelectedScreenshot(null);
        }

        this.game.levels.splice(index, 1);
        level.destroy();

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(deleteLevel(levelUUID));
        }
    }

    // Update game settings Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch hould be false for calls from the store.
    // Settings parameter: {
    //  description: string,
    //  view: enum-number,
    //  timeLimit: number,
    //  pointsGoal: number
    // }
    updateGameSettings(settings = {}, dispatch = true) {
        if ('title' in settings) {
            this.game.title = settings.title;
        }
        if ('description' in settings) {
            this.game.description = settings.description;
        }
        if ('goal' in settings) {
            this.game.goalSetting = settings.goal;
        }
        if ('score' in settings) {
            this.game.scoreSetting = settings.score;
        }

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(
                updateGameSettings({
                    title: this.game.title,
                    description: this.game.description,
                    goal: this.game.goalSetting,
                    score: this.game.scoreSetting
                })
            );
        }
    }

    // Update specific level settings Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch should be false for calls from the store.
    // levelUUID parameter: the UUID of the level to change settings of
    // Settings parameter: {
    //  description: string,
    //  timeLimit: number,
    //  pointsLimit: number,
    //  description: enum-number
    // }
    updateLevelSettings(levelUUID, settings = {}, dispatch = true) {
        const level = this.game.levels.findByUUID(levelUUID);
        if (level == null) {
            console.warn(`Level with UUID "${levelUUID}"could not be found.`);
            return;
        }

        if ('description' in settings) {
            level.description = settings.description;
        }
        if ('timeLimit' in settings) {
            level.timeLimit = settings.timeLimit;
        }
        if ('pointsGoal' in settings) {
            level.pointsGoal = settings.pointsGoal;
        }
        if ('view' in settings) {
            level.viewDirection = settings.view;
            level.player.requestNewVisuals();
            level.updateBackgroundVisuals();
            level.background.requestNewVisuals();
        }

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(setLevelSettings(settings));
            gamekitStore.dispatch(updateLevelDescription(levelUUID, level.description));
        }
    }

    // Update player settings Optional dispatch parameter (default: true) to dispatch to store.
    // Dispatch hould be false for calls from the store.
    // Settings parameter: {
    //  hat: id-string,
    //  texture: id-string,
    //  textureHue: number (0-360),
    //  lives: number,
    //  weight: number,
    //  bounce: number
    // }
    updatePlayerSettings(settings = {}, dispatch = true) {
        if ('hat' in settings) {
            const { hat } = settings;
            this.game.playerSettings.hat = hat;
            if (this.game.activeLevel) this.game.activeLevel.player.requestNewVisuals();
        }
        if ('texture' in settings) {
            const { texture } = settings;
            this.game.playerSettings.texture = texture;
            if (this.game.activeLevel) this.game.activeLevel.player.requestNewVisuals();
        }
        if ('textureHue' in settings) {
            const { textureHue } = settings;
            this.game.playerSettings.hue = textureHue;
            if (this.game.activeLevel) this.game.activeLevel.player.requestNewVisuals();
        }

        if ('lives' in settings) {
            let { lives } = settings;
            lives = Math.max(lives, playerSettings.livesMin);
            lives = Math.min(lives, playerSettings.livesMax);
            this.game.playerSettings.lives = lives;
        }
        if ('weight' in settings) {
            let { weight } = settings;
            weight = Math.max(weight, playerSettings.weightMin);
            weight = Math.min(weight, playerSettings.weightMax);
            this.game.playerSettings.weight = weight;
        }
        if ('bounce' in settings) {
            let { bounce } = settings;
            // Bounce is normalized, so min and max are hardcoded to 0.0 and 1.0
            bounce = Math.max(bounce, 0.0);
            bounce = Math.min(bounce, 1.0);
            this.game.playerSettings.bounce = bounce;
        }

        if (dispatch) {
            // Update de store
            gamekitStore.dispatch(
                updatePlayerSettings({
                    hat: this.game.playerSettings.hat,
                    texture: this.game.playerSettings.texture,
                    textureHue: this.game.playerSettings.hue,
                    lives: this.game.playerSettings.lives,
                    weight: this.game.playerSettings.weight,
                    bounce: this.game.playerSettings.bounce
                })
            );
        }
    }

    saveGame(onSuccess, onError) {
        const save = () => {
            const jsonGameData = this.game.serialize();
            const data = {
                title: this.game.title,
                description: this.game.description,
                gameData: jsonGameData
            };
            if (this.gameID !== '') {
                data.gameID = this.game.gameID;
            }
            if (this.originalGameID !== '') {
                data.originalGameID = this.originalGameID;
            }
            if (this.madeScreenshots.length > 0) {
                data.levelThumbnails = this.madeScreenshots;
            }
            if (this.selectedScreenshot) {
                data.thumbnail = this.selectedScreenshot;
            }
            if (this.deletedLevels.length > 0) {
                data.deletedLevels = this.deletedLevels;
            }

            let cancelled = false;

            // console.log({
            //         'Content-Type': 'application/json',
            //         // 'X-Requested-With': 'XMLHttpRequest',
            //         'X-CSRFToken': this.csrfToken,
            //         Authorization: this.authHash
            //     });

            fetch(this.game.settings.api.save, {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    // 'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRFToken': this.csrfToken,
                    Authorization: this.authHash
                },
                body: JSON.stringify(data)
            })
                .then(response => {
                    if (!response.ok || response.status >= 300) {
                        console.error(`Save game failed. Server responded with status code ${response.status}: ${response.statusText}`);
                        try {
                            localStorage.setItem('backup', JSON.stringify(jsonGameData));
                            console.log('Backup van het spel is opgeslagen in de local storage met item key: "backup".');
                        } catch (exception) {
                            console.warn('Failed to store a backup in the local storage.');
                        }
                        if (onError) {
                            onError(response.status);
                        }
                        cancelled = true;
                        return;
                    }
                    return response.json();
                })
                .then(json => {
                    if (!cancelled) {
                        this.lastSavedCopy = jsonGameData;
                        if ('creator' in json) {
                            this.setUser(json.creator, true);
                        }
                        this.deletedLevels = [];
                        this.madeScreenshots = [];
                        this.originalGameID = '';

                        if (json.gameID) {
                            this.game.gameID = json.gameID;
                            this.gameID = json.gameID;
                        }

                        if (onSuccess) onSuccess(json);
                    }
                });
        };
        this.loginUser(save, onError);
        /*
        window.LoginAPI.getHash(hash => {
            this.authHash = hash;
            if (hash) {
                save();
                /* this.authorized(
                    response => {
                        save();
                    },
                    error => response => {
                        LoginModal.show('login');
                        onError();
                    }
                );
            } else {
                window.LoginModal.show('login');
                onError();
            }
        }); */
    }

    publishGame(onSuccess, onError) {
        const publish = () => {
            const data = {
                gameID: this.game.gameID
            };
            let cancelled = false;
            fetch(this.game.settings.api.publish, {
                method: 'POST',
                cache: 'no-cache',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': this.csrfToken,
                    Authorization: this.authHash
                },
                body: JSON.stringify(data)
            })
                .then(response => {
                    if (!response.ok || response.status >= 300) {
                        console.error(`Publish game failed. Server responded with status code ${response.status}: ${response.statusText}`);
                        if (onError) {
                            onError(response.status);
                        }
                        cancelled = true;
                        return;
                    }
                    return response.json();
                })
                .then(json => {
                    if ('creator' in json) {
                        this.setUser(json.creator, true);
                    }
                })
                .catch(err => {
                    console.error(`Authorization failed. Server failed to respond.`);
                    if (onError) {
                        onError('No response');
                    }
                    cancelled = true;
                })
                .catch(err => {
                    console.error(`Authorization failed. Server failed to respond.`);
                    if (onError) {
                        onError('No response');
                    }
                    cancelled = true;
                })
                .then(json => {
                    if (!cancelled) {
                        console.log('Successful publish request returned:');
                        console.log(json);

                        if (onSuccess) onSuccess(json);
                    }
                });
        };
        this.saveGame(() => {
            this.loginUser(publish, onError);
        });
    }

    manualLoadGame(data) {
        let json = data;
        if (typeof data === 'string') json = JSON.parse(data);

        this.game.deserialize(json);
        if (json.gameID) this.gameID = json.gameID;

        this.updateGameSettings();
        this.updatePlayerSettings();
        this.dispatchLoadedLevels();
        if (json.levelThumbnails) {
            this.madeScreenshots = json.levelThumbnails;
            if (json.thumbnail) {
                this.selectedScreenshot = json.thumbnail;
            }
            this.makeScreenshotsBase64();
        }
    }

    loadGame(gameID, onSuccess, onError, forceAuthentication = false) {
        this.game.load(
            gameID,
            json => {
                this.updateGameSettings();
                this.updatePlayerSettings();
                this.dispatchLoadedLevels();
                this.gameID = gameID;
                if (json.levelThumbnails) {
                    this.madeScreenshots = json.levelThumbnails;
                    if (json.thumbnail) {
                        this.selectedScreenshot = json.thumbnail;
                    }
                    if (this.isCopy) {
                        this.makeScreenshotsBase64();
                    }
                }

                this.dispatchScreenshots();

                this.lastSavedCopy = this.game.serialize();

                if (onSuccess) onSuccess();
            },
            error => {
                if (onError) onError('De game kon niet geladen worden.');
            },
            forceAuthentication
        );
    }

    authorized(onSuccess, onError) {
        let cancelled = false;
        if (!this.authHash) {
            if (onError) {
                onError('No auth hash');
            }
        }
        fetch(`${this.game.settings.api.authorized}`, {
            method: 'GET',
            cache: 'no-cache',
            headers: {
                'Content-Type': 'application/json',
                Authorization: this.authHash
            }
        })
            .then(response => {
                if (!response.ok || response.status >= 300) {
                    if (response.status === 401) {
                        if (onError) {
                            onError(response.status);
                        }
                        return;
                    }
                    console.error(`Authorization failed. Server responded with status code ${response.status}: ${response.statusText}`);
                    if (onError) {
                        onError(response.status);
                    }
                    cancelled = true;
                    return;
                }
                return response.json();
            })
            .catch(err => {
                console.error(`Authorization failed. Server failed to respond.`);
                if (onError) {
                    onError('No response');
                }
                cancelled = true;
            })
            .then(json => {
                if (!cancelled) {
                    console.log('Successful authorization request returned:');
                    console.log(json);

                    if (onSuccess) onSuccess(json);
                }
            });
    }

    // Start testing the game.
    startTest(dispatch = true) {
        if (dispatch) {
            gamekitStore.dispatch(playerOpen());
        }
        this.gamePlayer.resetPlaySessionValues();
        this.gamePlayer.moveRenderer();
        this.game.setViewPortScaling(ViewPortScaling.GamePlayBest);
        this.levelEditor.detachFromLevel();
        this.gamePlayer.startPlay(
            () => {
                this.gamePlayer.stopPlay();
                this.stopTest();
            },
            () => {
                this.gamePlayer.stopPlay();
                this.stopTest();
            }
        );
    }

    // Stop testing the game.
    stopTest(dispatch = true) {
        this.gamePlayer.stopPlay();
        this.game._gsoSetPlayMode(false);
        if (this.game._hasBody) {
            this.game.destroyBody();
        }
        this.moveRenderer();
        this.game.destroyVisuals();
        this.game.createVisuals();
        this.game.setViewPortScaling(ViewPortScaling.NativeResolution);
        this.levelEditor.attachToLevel(this.game.activeLevel);
        if (dispatch) {
            gamekitStore.dispatch(playerClose());
        }
    }
}

export default new GameKit();
